/**
 * Auth-aware fetch wrapper installed over window.fetch (see main.tsx).
 *
 * It does three things on every request:
 *  1. Injects `x-tenant-id` and `Authorization: Bearer <access token>`.
 *  2. Injects `X-Perm-Version` once the permission document is loaded
 *     (INTRO-13: a purpose activated a moment ago is seen by every service).
 *  3. On a 401, performs ONE silent token refresh and retries the request —
 *     only when the request carried the user's token. A request that set its
 *     own token (an app's RPC) gets its 401 back untouched.
 *
 * The refresh is delegated to the store on purpose: for an impersonated session
 * it renews the impersonation rather than spending the superadmin's refresh
 * cookie, which would swap identity and tenant mid-session (see authStore).
 *
 * Why the retry matters: access tokens are short-lived (15 min). A proactive
 * timer refreshes ~60s before expiry, but browsers throttle timers in
 * backgrounded tabs, so a tab left open can fire a request with an
 * already-expired token. Without a retry that request just fails — which is how
 * "Create alert" (and the notification poll) silently 401'd. Retrying after a
 * refresh makes every authenticated request survive token expiry. A 401 means
 * the request was rejected, not processed, so retrying is safe even for POST.
 */
import { getAccessToken as realGetToken, getTenantId as realGetTenant, useAuthStore } from '../store/authStore';
import { isShareMode as realIsShareMode } from './shareMode';
import { inspectRefusal, tenantForRequest, urlForRequest } from './branchSession';
import { usePermissionStore } from '../store/permissionStore';

export interface AuthFetchDeps {
  getToken: () => string | null;
  getTenant: () => string;
  refresh: () => Promise<boolean>;
  isShareMode: () => boolean;
  /** Branch switcher: the tenant a request to `url` carries (lib/branchSession). */
  routeTenant?: (url: string, tenant: string) => string;
  /** Branch switcher: rewrite a `tenant_id=<main>` query param. */
  routeUrl?: (url: string) => string;
  /** Sees every final response; announces a protected-main refusal. */
  onResponse?: (resp: Response) => void;
  /** INTRO-13: `perm_version` of the permission document last read from
   *  auth-service. Sent as X-Perm-Version so a service whose cached copy is
   *  older reads it again instead of waiting out its cache (30 s). */
  getPermVersion?: () => number | null | undefined;
}

const defaultDeps: AuthFetchDeps = {
  getToken: realGetToken,
  getTenant: realGetTenant,
  refresh: () => useAuthStore.getState().refresh(),
  isShareMode: realIsShareMode,
  routeTenant: tenantForRequest,
  routeUrl: urlForRequest,
  onResponse: inspectRefusal,
  getPermVersion: () => usePermissionStore.getState().effective?.perm_version,
};

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return (input as Request).url ?? '';
}

/** Don't intercept the auth plumbing itself. `/auth/refresh` authenticates via
 *  the refresh cookie, and `/auth/impersonate` is what a refresh calls to renew
 *  an impersonated session — retrying either on 401 would recurse. */
function isAuthPlumbing(input: RequestInfo | URL): boolean {
  const url = urlOf(input);
  return url.includes('/auth/refresh') || url.includes('/auth/impersonate');
}

export function makeAuthFetch(
  orig: typeof fetch,
  deps: AuthFetchDeps = defaultDeps,
): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Share-mode viewers hit token-scoped endpoints; never inject tenant creds.
    if (deps.isShareMode()) return orig(input, init);

    const url = urlOf(input);
    // Inside a branch, a request that named main goes to the branch instead
    // (header and query param). Request objects keep their URL as built.
    const target = deps.routeUrl && (typeof input === 'string' || input instanceof URL)
      ? deps.routeUrl(url) : input;
    const sentToken = deps.getToken();
    // A caller may set Authorization itself. Its own copy of the user token is
    // still the user's session; any other token (an app's, an impersonation
    // admin's) is one a user refresh cannot renew.
    const callerAuth = new Headers(init?.headers).get('Authorization');
    const userAuth = callerAuth === null || (!!sentToken && callerAuth === `Bearer ${sentToken}`);
    const send = (token: string | null) => {
      const headers = new Headers(init?.headers);
      const tenant = headers.get('x-tenant-id') || deps.getTenant();
      headers.set('x-tenant-id', deps.routeTenant ? deps.routeTenant(url, tenant) : tenant);
      if (token && userAuth) headers.set('Authorization', `Bearer ${token}`);
      const permVersion = deps.getPermVersion?.();
      if (token && permVersion && permVersion > 0 && !headers.has('X-Perm-Version')) {
        headers.set('X-Perm-Version', String(permVersion));
      }
      return orig(target, { ...init, headers });
    };

    let resp = await send(sentToken);

    // Expired access token → refresh once and retry. Only when we actually sent
    // the user's token, the user is still signed in (a refresh after logout
    // would bring the session back), and never for the refresh call itself.
    if (resp.status === 401 && sentToken && userAuth && deps.getToken() && !isAuthPlumbing(input)) {
      const refreshed = await deps.refresh().catch(() => false);
      if (refreshed) resp = await send(deps.getToken());
    }
    deps.onResponse?.(resp);
    return resp;
  };
}
